import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

/**
 * Email-First Login Controller
 *
 * Implements the email-first login flow with smooth transitions:
 * 1. User enters email
 * 2. POST to /auth/check_email to determine login method
 * 3a. If password → show password form with slide animation
 * 3b. If OIDC → auto-submit SSO redirect form
 */
export default class extends Controller {
  static targets = [
    "emailStep",
    "passwordStep",
    "ssoStep",
    "emailInput",
    "hiddenEmailInput",
    "passwordInput",
    "emailDisplay",
    "emailDisplayHeader",
    "emailError",
    "continueButton",
    "createAccountLink",
  ]

  static values = {
    checkEmailUrl: String,
    // Translated by the view (CONEK-6681) — no English literals in here.
    requestFailedMessage: String,
    networkErrorMessage: String,
    checkingLabel: String,
    continueLabel: String,
  }

  connect() {
    this.currentStep = "email"
  }

  /**
   * Handle email form submission
   * Checks email domain to determine login method
   */
  async checkEmail(event) {
    event.preventDefault()

    const email = this.emailInputTarget.value.trim()
    if (!email) return

    // Show loading state
    this.continueButtonTarget.disabled = true
    this.continueButtonTarget.textContent = this.checkingLabelValue
    this.clearError()

    try {
      let response = await this.postCheckEmail(email)
      let error = response.ok ? null : await response.json.catch(() => null)

      // CONEK-8514 — the page's token can belong to another session than the
      // cookie (two requests raced for a new session after the old one ended).
      // The server answers with this session's token: adopt it and retry once.
      if (error?.code === "invalid_authenticity_token" && error.csrf_token) {
        this.adoptCsrfToken(error.csrf_token)
        response = await this.postCheckEmail(email)
        error = response.ok ? null : await response.json.catch(() => null)
      }

      if (response.ok) {
        const result = await response.json
        this.handleLoginMethodResponse(result, email)
      } else {
        // A non-JSON error page (e.g. an HTML 500, or a proxy's error page)
        // makes `response.json` reject, so `error` is null. The server DID
        // answer, so that is a failed request, not a network error
        // (CONEK-7606) — only a throw from `post()` itself means no response.
        // `error.error` is server-translated (auth.errors.*) beside a stable `code`.
        this.showError(error?.error || this.requestFailedMessageValue)
      }
    } catch (_error) {
      this.showError(this.networkErrorMessageValue)
    } finally {
      this.continueButtonTarget.disabled = false
      this.continueButtonTarget.textContent = this.continueLabelValue
    }
  }

  postCheckEmail(email) {
    return post(this.checkEmailUrlValue, {
      body: JSON.stringify({ email }),
      contentType: "application/json",
      responseKind: "json",
    })
  }

  /**
   * Puts [token] where the next requests read it: the meta tag `post()` sends
   * as X-CSRF-Token, and the hidden fields of the password (and any other)
   * form on the page, so the sign-in POST that follows matches the cookie too.
   */
  adoptCsrfToken(token) {
    document
      .querySelector('meta[name="csrf-token"]')
      ?.setAttribute("content", token)
    document
      .querySelectorAll('input[name="authenticity_token"]')
      .forEach((input) => (input.value = token))
  }

  /**
   * Handle the response from check_email endpoint
   */
  handleLoginMethodResponse(result, email) {
    if (result.login_method === "oidc") {
      // SSO login - redirect to external provider
      this.showSsoStep(email, result.sso_url)
    } else {
      // Password login - show password form
      this.showPasswordStep(email, result.allow_password_reset)
    }
  }

  /**
   * Show password step with smooth transition
   */
  showPasswordStep(email, _allowPasswordReset = true) {
    this.hideStep(this.emailStepTarget)
    this.showStep(this.passwordStepTarget)
    this.emailDisplayTarget.textContent = email

    // Set hidden email input for form submission
    if (this.hasHiddenEmailInputTarget) {
      this.hiddenEmailInputTarget.value = email
    }

    // Set email in header if target exists
    if (this.hasEmailDisplayHeaderTarget) {
      this.emailDisplayHeaderTarget.textContent = email
    }

    // Focus password input after transition
    setTimeout(() => {
      this.passwordInputTarget.focus()
    }, 300)

    // Hide create account link (B2B system)
    if (this.hasCreateAccountLinkTarget) {
      this.createAccountLinkTarget.classList.add("hidden")
    }

    this.currentStep = "password"
  }

  /**
   * Show SSO redirect step and route to the provider.
   *
   * On Hotwire Native Android, the OAuth `state` cookie that OmniAuth's
   * request phase sets on `/auth/<provider>` would otherwise land in the
   * WebView's session jar; the eventual callback runs in a Chrome Custom
   * Tab with a separate cookie jar, OmniAuth rejects with `csrf_detected`,
   * and native SSO is broken. CONEK-4126.
   *
   * Fix has two coordinated halves:
   *   1. (here) Bypass Turbo Drive with `window.location.href`. Turbo's
   *      form-submission path does an XHR-style fetch and only proposes a
   *      visit when it sees the cross-origin redirect target — so the
   *      `/auth/<provider>` URL never reaches the native route handler.
   *      A direct `window.location.href` assignment triggers a real
   *      browser navigation, which fires the WebView's
   *      `shouldOverrideUrlLoading`, which proposes the visit to the
   *      native router.
   *   2. (Android shell) `OidcStartRouteDecisionHandler` matches same-host
   *      `/auth/<provider>` URLs and opens them in a Chrome Custom Tab.
   *      Chrome then makes the FIRST request to `/auth/<provider>`, the
   *      state cookie lives in Chrome's jar from the start, the callback
   *      validates normally, the bridge mint chain works.
   *
   * Desktop browsers: `window.location.href` is just a full-page nav.
   * Identical behavior to the original form-submit path — no regression.
   */
  showSsoStep(email, ssoUrl) {
    this.hideStep(this.emailStepTarget)
    this.showStep(this.ssoStepTarget)

    setTimeout(() => {
      // `window.location.href` is the most reliable way to force a real
      // browser navigation (not a Turbo Drive XHR). Turbo intercepts link
      // clicks and form submits but NOT direct location assignments.
      console.log("[CONEK-4126] showSsoStep navigating to", ssoUrl)
      window.location.href = ssoUrl
    }, 800) // Delay for transition + user to see redirect message

    this.currentStep = "sso"
  }

  /**
   * Go back to email step with smooth transition
   */
  backToEmail(event) {
    event.preventDefault()

    this.hideStep(this.passwordStepTarget)
    this.showStep(this.emailStepTarget)
    this.passwordInputTarget.value = ""
    this.clearError()

    if (this.hasCreateAccountLinkTarget) {
      this.createAccountLinkTarget.classList.remove("hidden")
    }

    this.currentStep = "email"

    // Focus email input after transition
    setTimeout(() => {
      this.emailInputTarget.focus()
    }, 300)
  }

  /**
   * Hide a step with fade-out and slide-up animation
   */
  hideStep(element) {
    element.classList.add("opacity-0", "translate-y-2")
    setTimeout(() => {
      element.classList.add("hidden")
    }, 300)
  }

  /**
   * Show a step with fade-in and slide-down animation
   */
  showStep(element) {
    // First, remove hidden and set initial animation state
    element.classList.remove("hidden")
    element.classList.add("opacity-0", "translate-y-2")

    // Force a reflow to ensure the transition triggers — assigning to `void`
    // sidesteps `no-unused-expressions` while preserving the layout read.
    void element.offsetHeight

    // Then animate in
    requestAnimationFrame(() => {
      element.classList.remove("opacity-0", "translate-y-2")
    })
  }

  /**
   * Show error message
   */
  showError(message) {
    if (this.hasEmailErrorTarget) {
      this.emailErrorTarget.textContent = message
      this.emailErrorTarget.classList.remove("hidden")
    }
  }

  /**
   * Clear error message
   */
  clearError() {
    if (this.hasEmailErrorTarget) {
      this.emailErrorTarget.textContent = ""
      this.emailErrorTarget.classList.add("hidden")
    }
  }
}
